import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiRefreshCw,
} from 'react-icons/fi';
import { AdsOpsPanel } from '@/components/admin/AdsOpsPanel';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { track } from '@/lib/analytics';
import { apiJson } from '@/lib/api';
import type {
  AdminCheckGoogleAdsCredentialsResponse,
  AdminGoogleAdsMetricsResponse,
  GoogleAdsCampaignRow,
  GoogleAdsDailyPoint,
} from '@/lib/types';

// Stored in the URL as a string (TanStack Router's `location.search` is a
// union over every route's schema, and several callers — e.g.
// `admin/users/$userId/audit.tsx` — feed that object straight into
// `new URLSearchParams(...)`, which only accepts `Record<string, string>`).
// Keeping `days` as a string literal keeps that contract intact while still
// giving us a finite, validated set of values.
const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];

const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));

function microsToCurrency(
  micros: number | null | undefined,
  currencyCode: string | null | undefined,
): string {
  if (micros == null) return '—';
  const amount = micros / 1_000_000;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode || 'USD',
    maximumFractionDigits: amount >= 100 ? 0 : 2,
  }).format(amount);
}

function formatNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPercent(rate: number | null | undefined): string {
  if (rate == null) return '—';
  const pct = rate * 100;
  if (pct === 0) return '0%';
  if (pct < 0.1) return `${pct.toFixed(3)}%`;
  if (pct < 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'highlight';
}) {
  return (
    <div
      className={`rounded-lg shadow-sm border p-4 ${
        tone === 'highlight'
          ? 'border-nova-primary/30 bg-nova-accent'
          : 'border-nova-border bg-nova-card'
      }`}
    >
      <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

const CAMPAIGN_STATUS_CHIP: Record<string, string> = {
  ENABLED: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/80',
  PAUSED: 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80',
  REMOVED: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200/80',
};

function statusChip(status: string) {
  const cls =
    CAMPAIGN_STATUS_CHIP[status] ??
    'bg-slate-100 text-slate-800 ring-1 ring-slate-200/80';
  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}
    >
      {status.toLowerCase()}
    </span>
  );
}

/**
 * A single SVG bar-chart pane (e.g. spend per day) with an interactive hover
 * label. Hovering a bar highlights it and floats a label above it showing the
 * date and value. Keeps the file dependency-free — no charting lib needed.
 */
function ChartPane({
  title,
  daily,
  valueFn,
  max,
  color,
  formatTip,
}: {
  title: string;
  daily: GoogleAdsDailyPoint[];
  valueFn: (d: GoogleAdsDailyPoint) => number;
  max: number;
  color: string;
  formatTip: (d: GoogleAdsDailyPoint) => string;
}) {
  const [hovered, setHovered] = useState<number | null>(null);

  const width = 640;
  const height = 140;
  const padX = 36;
  const padY = 18;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const n = daily.length;
  const barGap = 2;
  const barW = Math.max(2, innerW / n - barGap);

  const active = hovered != null ? daily[hovered] : null;
  let hoverLabel: { x: number; barTopY: number; text: string } | null = null;
  if (active && hovered != null) {
    const v = valueFn(active);
    const h = max > 0 ? (v / max) * innerH : 0;
    const x = padX + hovered * (innerW / n) + barGap / 2;
    const y = height - padY - h;
    hoverLabel = {
      x: x + barW / 2,
      barTopY: y,
      text: `${formatDateLabel(active.date)} · ${formatTip(active)}`,
    };
  }

  return (
    <div className='min-w-0 flex-1'>
      <div className='flex items-baseline justify-between mb-1'>
        <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
          {title}
        </p>
        <p className='text-[11px] text-nova-muted-foreground'>
          {formatDateLabel(daily[0].date)} – {formatDateLabel(daily[n - 1].date)}
        </p>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className='w-full h-32'
        role='img'
        aria-label={title}
        onMouseLeave={() => setHovered(null)}
      >
        <line
          x1={padX}
          y1={height - padY}
          x2={width - padX}
          y2={height - padY}
          stroke='#e5e7eb'
        />
        {daily.map((d, i) => {
          const v = valueFn(d);
          const h = max > 0 ? (v / max) * innerH : 0;
          const x = padX + i * (innerW / n) + barGap / 2;
          const y = height - padY - h;
          const isHovered = hovered === i;
          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(0, h)}
                fill={color}
                opacity={hovered != null && !isHovered ? 0.45 : 1}
                rx={1}
              />
              {/* Wider transparent hit area so thin bars are easy to hover. */}
              <rect
                x={padX + i * (innerW / n)}
                y={padY}
                width={innerW / n}
                height={innerH}
                fill='transparent'
                onMouseEnter={() => setHovered(i)}
              >
                <title>
                  {formatDateLabel(d.date)}
                  {'\n'}
                  {formatTip(d)}
                </title>
              </rect>
            </g>
          );
        })}
        {hoverLabel ? (
          <BarChartHoverLabel
            x={hoverLabel.x}
            barTopY={hoverLabel.barTopY}
            minX={padX + 30}
            maxX={width - padX - 30}
            text={hoverLabel.text}
          />
        ) : null}
      </svg>
    </div>
  );
}

/**
 * Compact two-pane SVG chart: spend bars (left) and conversions bars (right)
 * sharing the same x-axis (the day index).
 */
function DailyMiniChart({
  daily,
  currencyCode,
}: {
  daily: GoogleAdsDailyPoint[];
  currencyCode: string | null;
}) {
  if (!daily.length) {
    return (
      <p className='text-sm text-nova-muted-foreground'>
        No daily activity in this date range yet.
      </p>
    );
  }

  const maxCost = Math.max(1, ...daily.map((d) => d.cost_micros));
  const maxConv = Math.max(0.0001, ...daily.map((d) => d.conversions));

  return (
    <div className='flex flex-col md:flex-row gap-4'>
      <ChartPane
        title='Spend per day'
        daily={daily}
        valueFn={(d) => d.cost_micros}
        max={maxCost}
        color='#018A9D'
        formatTip={(d) =>
          `${microsToCurrency(d.cost_micros, currencyCode)} · ${d.clicks.toLocaleString()} clicks`
        }
      />
      <ChartPane
        title='Conversions per day'
        daily={daily}
        valueFn={(d) => d.conversions}
        max={maxConv}
        color='#0ea5e9'
        formatTip={(d) =>
          `${formatNumber(d.conversions)} conversions · ${d.clicks.toLocaleString()} clicks`
        }
      />
    </div>
  );
}

function uploadPipelineSummary(
  checkResult: AdminCheckGoogleAdsCredentialsResponse | undefined,
): { text: string; tone: 'good' | 'warn' | 'alert' | 'muted' } | null {
  const pipe = checkResult?.upload_pipeline;
  if (!checkResult?.ok || !pipe) return null;
  if (pipe.query_error) {
    return {
      text: 'Upload pipeline health could not be queried.',
      tone: 'warn',
    };
  }
  const days =
    pipe.days_since_last_upload == null
      ? 'never'
      : pipe.days_since_last_upload >= 10000
        ? 'never'
        : `${pipe.days_since_last_upload}d ago`;
  const text = `Offline uploads: ${pipe.pending_uploads} pending · last upload ${days}`;
  if (pipe.silence_alert_warranted || checkResult.upload_silence_alerted) {
    return { text, tone: 'alert' };
  }
  if (pipe.pending_uploads > 0) {
    return { text, tone: 'warn' };
  }
  return { text, tone: 'good' };
}

function CredentialsBanner({
  metrics,
  onRunCheck,
  checkResult,
  isChecking,
}: {
  metrics: AdminGoogleAdsMetricsResponse;
  onRunCheck: () => void;
  checkResult: AdminCheckGoogleAdsCredentialsResponse | undefined;
  isChecking: boolean;
}) {
  const missing = Object.entries(metrics.env_vars)
    .filter(([, present]) => !present)
    .map(([name]) => name);
  const pipeline = uploadPipelineSummary(checkResult);

  if (metrics.ok) {
    return (
      <div className='rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 space-y-2'>
        <div className='flex items-start gap-2'>
          <FiCheckCircle className='h-4 w-4 mt-0.5 shrink-0' aria-hidden />
          <div className='min-w-0 flex-1'>
            <p>
              <span className='font-semibold'>Connected</span> · account{' '}
              <span className='font-mono'>{metrics.customer_id_masked}</span>
              {metrics.login_customer_id_masked &&
              metrics.login_customer_id_masked !== '(unset)'
                ? ` (manager ${metrics.login_customer_id_masked})`
                : ''}
              {metrics.currency_code ? ` · ${metrics.currency_code}` : ''}
              {metrics.time_zone ? ` · ${metrics.time_zone}` : ''}
            </p>
            {pipeline ? (
              <p
                className={`mt-1 text-xs ${
                  pipeline.tone === 'alert'
                    ? 'text-rose-800'
                    : pipeline.tone === 'warn'
                      ? 'text-amber-900'
                      : 'text-emerald-800'
                }`}
              >
                {pipeline.text}
                {checkResult?.upload_silence_dedup_suppressed
                  ? ' · silence alert suppressed (recent)'
                  : ''}
              </p>
            ) : (
              <p className='mt-1 text-xs text-emerald-800/80'>
                Run a credentials check to load offline-upload pipeline health.
              </p>
            )}
          </div>
          <button
            type='button'
            onClick={onRunCheck}
            disabled={isChecking}
            className='inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-nova-card px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-60 shrink-0'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${isChecking ? 'animate-spin' : ''}`}
            />
            {isChecking ? 'Checking…' : 'Check credentials'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
      <div className='flex items-start gap-2'>
        <FiAlertTriangle className='h-4 w-4 mt-0.5 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-2'>
          <p>
            <span className='font-semibold'>
              Google Ads metrics unavailable.
            </span>{' '}
            {metrics.error ?? 'Unknown error.'}
          </p>
          {missing.length > 0 ? (
            <p className='text-xs'>
              Missing env vars:{' '}
              <span className='font-mono'>{missing.join(', ')}</span>. Set them
              in the API + Celery deployment env, then redeploy.
            </p>
          ) : null}
          <div className='flex flex-wrap items-center gap-2 pt-1'>
            <button
              type='button'
              onClick={onRunCheck}
              disabled={isChecking}
              className='inline-flex items-center gap-1.5 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60'
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Checking…' : 'Run credentials check'}
            </button>
            {checkResult ? (
              <span
                className={`text-xs ${checkResult.ok ? 'text-emerald-800' : 'text-rose-800'}`}
              >
                {checkResult.ok
                  ? 'OAuth + API call succeeded.'
                  : `Failed at ${checkResult.step ?? '?'}: ${checkResult.message ?? checkResult.reason ?? 'unknown error'}`}
              </span>
            ) : null}
          </div>
          {pipeline ? (
            <p
              className={`text-xs ${
                pipeline.tone === 'alert'
                  ? 'text-rose-800'
                  : pipeline.tone === 'warn'
                    ? 'text-amber-900'
                    : 'text-emerald-800'
              }`}
            >
              {pipeline.text}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function AdminAdsPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/ads' });
  const { days } = Route.useSearch();

  useEffect(() => {
    track('Ads Admin Screen');
  }, []);
  const [credentialsResult, setCredentialsResult] = useState<
    AdminCheckGoogleAdsCredentialsResponse | undefined
  >(undefined);

  const daysNum = Number(days);
  const metricsQuery = useQuery({
    queryKey: ['admin', 'google-ads-metrics', daysNum],
    queryFn: () =>
      apiJson<AdminGoogleAdsMetricsResponse>(
        getToken,
        `/v1/admin/google-ads/metrics?days=${daysNum}`,
      ),
  });

  const credentialsMutation = useMutation({
    mutationFn: () =>
      apiJson<AdminCheckGoogleAdsCredentialsResponse>(
        getToken,
        '/v1/admin/check-google-ads-credentials',
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      setCredentialsResult(data);
      // Re-pull metrics in case credentials are now usable.
      metricsQuery.refetch();
    },
    onError: () => {
      setCredentialsResult({
        ok: false,
        reason: 'request_error',
        step: 'request',
        error_code: null,
        message: 'Could not reach /v1/admin/check-google-ads-credentials.',
        env_vars: {},
        alerted: false,
        dedup_suppressed: false,
      });
    },
  });

  const data = metricsQuery.data;
  const totals = data?.totals;
  const currency = data?.currency_code ?? null;

  const sortedCampaigns = useMemo<GoogleAdsCampaignRow[]>(
    () =>
      data
        ? [...data.campaigns].sort((a, b) => b.cost_micros - a.cost_micros)
        : [],
    [data],
  );

  const onChangeRange = (next: RangeDays) => {
    if (next === days) return;
    track('Ads Admin Range Changed', { days: next });
    navigate({ search: { days: next } });
  };

  const daysHint = `${daysNum}-day total`;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-nova-foreground'>Google Ads</h1>
          <p className='text-sm text-nova-muted-foreground mt-1'>
            Ads HQ: standing orders, knobs, Harbor-truth, then Google spend.
            Talk to an agent to change strategy. Ads-UI conversions are a
            footnote — do not optimize them.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <div className='inline-flex rounded-md border border-nova-border bg-nova-card p-0.5 shadow-sm'>
            {RANGE_OPTIONS.map((opt) => {
              const active = opt.days === days;
              return (
                <button
                  key={opt.days}
                  type='button'
                  onClick={() => onChangeRange(opt.days)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded ${
                    active
                      ? 'bg-nova-accent text-nova-primary'
                      : 'text-nova-muted-foreground hover:text-nova-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type='button'
            onClick={() => {
              track('Ads Admin Refresh Clicked');
              metricsQuery.refetch();
            }}
            disabled={metricsQuery.isFetching}
            className='inline-flex items-center gap-1.5 rounded-md border border-nova-input bg-nova-card px-3 py-1.5 text-xs font-semibold text-nova-foreground hover:bg-nova-accent disabled:opacity-60'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${metricsQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      <AdsOpsPanel days={daysNum} />

      {metricsQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='animate-spin h-10 w-10 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : metricsQuery.isError || !data ? (
        <p className='text-red-600'>Could not load Google Ads account metrics.</p>
      ) : (
        <>
          <CredentialsBanner
            metrics={data}
            onRunCheck={() => {
              track('Ads Admin Credentials Check Clicked');
              credentialsMutation.mutate();
            }}
            checkResult={credentialsResult}
            isChecking={credentialsMutation.isPending}
          />

          <div className='text-xs text-nova-muted-foreground flex items-center gap-1.5'>
            <FiInfo className='h-3.5 w-3.5' aria-hidden />
            <span>
              {data.start_date} → {data.end_date} (UTC). Cost in{' '}
              {currency ?? 'USD'}.
            </span>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3'>
            <StatTile
              label='Spend'
              value={microsToCurrency(totals?.cost_micros ?? 0, currency)}
              hint={daysHint}
              tone='highlight'
            />
            <StatTile
              label='Impressions'
              value={formatNumber(totals?.impressions ?? 0)}
            />
            <StatTile
              label='Clicks'
              value={formatNumber(totals?.clicks ?? 0)}
              hint={`CTR ${formatPercent(totals?.ctr ?? null)}`}
            />
            <StatTile
              label='Avg CPC'
              value={microsToCurrency(totals?.avg_cpc_micros ?? null, currency)}
            />
            <StatTile
              label='Ads-UI conv.'
              value={formatNumber(totals?.conversions ?? 0)}
              hint='Google-reported — not Harbor CAC'
            />
            <StatTile
              label='Conv. value'
              value={
                totals
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: currency || 'USD',
                      maximumFractionDigits: 2,
                    }).format(totals.conversions_value)
                  : '—'
              }
              hint='Reported by Google Ads'
            />
            <StatTile
              label='Cost / conv.'
              value={microsToCurrency(
                totals?.cost_per_conversion_micros ?? null,
                currency,
              )}
            />
            <StatTile
              label='ROAS'
              value={
                totals && totals.cost_micros > 0
                  ? `${(totals.conversions_value / (totals.cost_micros / 1_000_000)).toFixed(2)}x`
                  : '—'
              }
              hint='Conv. value / spend'
            />
          </div>

          <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
            <DailyMiniChart daily={data.daily} currencyCode={currency} />
          </div>

          <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border overflow-hidden'>
            <div className='px-4 py-3 border-b border-nova-border flex items-center justify-between'>
              <h2 className='text-sm font-semibold text-nova-foreground'>
                Campaigns
                <span className='ml-2 text-xs font-normal text-nova-muted-foreground'>
                  ({sortedCampaigns.length})
                </span>
              </h2>
              <p className='text-xs text-nova-muted-foreground'>Sorted by spend</p>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-nova-muted text-xs uppercase text-nova-muted-foreground'>
                  <tr>
                    <th scope='col' className='px-4 py-2 text-left'>
                      Campaign
                    </th>
                    <th scope='col' className='px-4 py-2 text-left'>
                      Status
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      Impr.
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      Clicks
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      CTR
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      Conv.
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      Spend
                    </th>
                    <th scope='col' className='px-4 py-2 text-right'>
                      Ads-UI CPA
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-nova-border'>
                  {sortedCampaigns.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className='px-4 py-8 text-center text-nova-muted-foreground'
                      >
                        No campaign activity in this date range.
                      </td>
                    </tr>
                  ) : (
                    sortedCampaigns.map((c) => {
                      const ctr =
                        c.impressions > 0 ? c.clicks / c.impressions : null;
                      const cpa =
                        c.conversions > 0
                          ? c.cost_micros / c.conversions
                          : null;
                      return (
                        <tr key={c.id} className='hover:bg-nova-accent'>
                          <td className='px-4 py-2'>
                            <p className='font-medium text-nova-foreground'>{c.name}</p>
                            {c.advertising_channel_type ? (
                              <p className='text-xs text-nova-muted-foreground'>
                                {c.advertising_channel_type
                                  .replace(/_/g, ' ')
                                  .toLowerCase()}
                              </p>
                            ) : null}
                          </td>
                          <td className='px-4 py-2'>{statusChip(c.status)}</td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                            {c.impressions.toLocaleString()}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                            {c.clicks.toLocaleString()}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                            {formatPercent(ctr)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                            {formatNumber(c.conversions)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground font-semibold'>
                            {microsToCurrency(c.cost_micros, currency)}
                          </td>
                          <td className='px-4 py-2 text-right tabular-nums text-nova-foreground'>
                            {microsToCurrency(cpa, currency)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/ads')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { days: RangeDays } => {
    const raw = String(search.days ?? '');
    return {
      days: ALLOWED_DAYS.has(raw) ? (raw as RangeDays) : '30',
    };
  },
  component: AdminAdsPage,
});
